// Audit log — paginated, filterable read of every mutating admin action.
// Action keys come from the backend audit-service (user.role.grant,
// photoshoot.update, settings.ip_allowlist.update, etc.). We don't try
// to enumerate them here — the input is free-text, the server filters
// exactly.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { request } from '@/lib/api'
import type { AdminAuditLogPage } from '@/types/api'

export function AuditLogPage() {
  const [actorId, setActorId] = useState('')
  const [action, setAction] = useState('')
  const [targetType, setTargetType] = useState('')
  const [cursor, setCursor] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['admin', 'audit-log', { actorId, action, targetType, cursor }],
    queryFn: () =>
      request<AdminAuditLogPage>('/admin/audit-log', {
        query: {
          actor_id: actorId || undefined,
          action: action || undefined,
          target_type: targetType || undefined,
          cursor: cursor ?? undefined,
          limit: 50,
        },
      }),
  })

  return (
    <div className="p-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">Audit log</h1>
        <p className="text-sm text-ink-400">Every mutating admin action — newest first.</p>
      </header>

      <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <input
          placeholder="actor user id (uuid)"
          value={actorId}
          onChange={(e) => { setActorId(e.target.value); setCursor(null) }}
          className="rounded-md border border-ink-200 px-3 py-2 font-mono text-xs focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
        />
        <input
          placeholder="action (e.g. user.role.grant)"
          value={action}
          onChange={(e) => { setAction(e.target.value); setCursor(null) }}
          className="rounded-md border border-ink-200 px-3 py-2 font-mono text-xs focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
        />
        <input
          placeholder="target_type (e.g. user, photoshoot)"
          value={targetType}
          onChange={(e) => { setTargetType(e.target.value); setCursor(null) }}
          className="rounded-md border border-ink-200 px-3 py-2 font-mono text-xs focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
        />
      </div>

      <div className="overflow-x-auto rounded-xl border border-ink-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-ink-100 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-400">
            <tr>
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Actor</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Target</th>
              <th className="px-4 py-3">IP</th>
              <th className="px-4 py-3">Metadata</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-ink-400">Loading…</td></tr>
            )}
            {query.data?.items.map((row) => (
              <tr key={row.id} className="border-b border-ink-100 last:border-0 align-top">
                <td className="whitespace-nowrap px-4 py-3 text-xs text-ink-500">
                  {new Date(row.created_at).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-xs">
                  {row.actor_email || row.actor_name ? (
                    <div>
                      <div className="font-medium text-ink-700">{row.actor_email ?? row.actor_name}</div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-ink-400">
                        {row.actor_name && row.actor_email && <span>{row.actor_name}</span>}
                        {row.actor_role && (
                          <span className="rounded bg-ink-100 px-1.5 py-0.5 font-medium text-ink-600">{row.actor_role}</span>
                        )}
                      </div>
                    </div>
                  ) : row.actor_user_id ? (
                    <span className="font-mono text-ink-400" title="user record not found (hard-deleted)">{row.actor_user_id}</span>
                  ) : (
                    <span className="italic text-ink-300">system / cli</span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-xs">{row.action}</td>
                <td className="px-4 py-3 text-xs">
                  {row.target_type && <span className="text-ink-500">{row.target_type}</span>}
                  {row.target_id && (
                    <div className="font-mono text-[10px] text-ink-400">{row.target_id}</div>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-ink-500">{row.ip ?? '—'}</td>
                <td className="px-4 py-3">
                  <pre className="max-w-xs overflow-x-auto rounded bg-ink-50 px-2 py-1 font-mono text-[10px] text-ink-700">
                    {JSON.stringify(row.event_metadata, null, 0)}
                  </pre>
                </td>
              </tr>
            ))}
            {!query.isLoading && query.data?.items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-ink-400">No audit-log rows match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {query.data?.next_cursor && (
        <div className="mt-4 text-right">
          <button
            type="button"
            onClick={() => setCursor(query.data!.next_cursor!)}
            className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:border-ink-300"
          >
            Next page →
          </button>
        </div>
      )}
    </div>
  )
}
